import { useEffect, useState, useCallback, useRef } from 'react';
import { apiGet, apiDelete, API_BASE } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Trash2, Search, RefreshCw, FileText, AlertTriangle, CheckCircle2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

// PDF files are served from the backend origin (API_BASE strips the trailing /api).
const FILE_ORIGIN = API_BASE.replace(/\/api$/, '');

const BOARDS = [
  { k: 'cbse', label: 'CBSE' },
  { k: 'icse', label: 'ICSE' },
  { k: 'ib', label: 'IB' },
  { k: 'igcse', label: 'Cambridge IGCSE' },
  { k: 'state_board', label: 'State Board' },
  { k: 'nios', label: 'NIOS' },
];
const CLASSES = ['1','2','3','4','5','6','7','8','9','10','11','12'];

export default function ContentLibrary() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [boardFilter, setBoardFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [deleting, setDeleting] = useState({});

  const inFlightRef = useRef(null);

  const load = useCallback(async () => {
    // Dedupe concurrent calls (prevents the dual-useEffect race) + retry-with-
    // backoff on transient failures + show the real error code in the toast.
    if (inFlightRef.current) return inFlightRef.current;
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (boardFilter) params.append('board', boardFilter);
    if (classFilter) params.append('class_level', classFilter);
    if (subjectFilter) params.append('subject', subjectFilter);
    params.append('limit', '300');
    const url = `/syllabus/library?${params.toString()}`;

    const run = (async () => {
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise(res => setTimeout(res, attempt === 1 ? 700 : 1500));
        try {
          const r = await apiGet(url);
          setItems(r.items || []);
          setTotal(r.total || 0);
          setLoading(false);
          return r;
        } catch (e) {
          lastErr = e;
          if (e?.status === 401 || e?.status === 403) break;
        }
      }
      setLoading(false);
      const code = lastErr?.status ? `HTTP ${lastErr.status}` : (lastErr?.isNetworkError ? 'network error' : (lastErr?.message || 'unknown error'));
      toast.error(`Failed to load library (${code})`, {
        action: { label: 'Retry', onClick: () => load() },
        duration: 8000,
      });
      throw lastErr;
    })().finally(() => { inFlightRef.current = null; });

    inFlightRef.current = run;
    return run.catch(() => {});
  }, [q, boardFilter, classFilter, subjectFilter]);

  useEffect(() => { load(); }, [load]);

  const deleteOne = async (sub, alsoFile = true) => {
    const who = `${sub.subject || '?'} · Class ${sub.class_level || '?'} · ${sub.topic_name || prettyName(sub.file_name) || '?'}`;
    if (!window.confirm(`Delete "${who}"?\n\nThis will remove:\n• all rendered page images\n• all RAG text chunks\n• the submission record${alsoFile ? '\n• the underlying PDF file from disk' : ''}\n\nThis action is irreversible.`)) return;
    setDeleting(prev => ({ ...prev, [sub.submission_id]: true }));
    try {
      const r = await apiDelete(`/syllabus/submissions/${sub.submission_id}?delete_file=${alsoFile ? 'true' : 'false'}`);
      toast.success(`Deleted — ${r.page_images_deleted} pages, ${r.chunks_deleted} chunks${r.file_removed_from_disk ? ', file removed from disk' : ''}`);
      setItems(prev => prev.filter(x => x.submission_id !== sub.submission_id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e) {
      const msg = e?.message === 'UNAUTHORIZED' ? 'Session expired — please re-login' : 'Delete failed';
      toast.error(msg);
    }
    setDeleting(prev => { const n = { ...prev }; delete n[sub.submission_id]; return n; });
  };

  // Legacy rows may carry the 8-char uuid prefix (e.g. "acf26d1e_CBSE-...") that the
  // upload handler adds to on-disk filenames. Strip it for display so admins see the
  // original name they uploaded — safer when deciding what to delete.
  const prettyName = (name) => (name || '').replace(/^[a-f0-9]{8}_/i, '');

  const subjectOptions = Array.from(new Set(items.map(i => i.subject).filter(Boolean))).sort();

  const totalSizeMb = items.reduce((s, i) => s + (i.size_mb || 0), 0);
  const onDiskCount = items.filter(i => i.on_disk).length;

  return (
    <div className="space-y-5" data-testid="content-library">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-indigo-500" />
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900 tracking-tight">Content Library</h3>
            <p className="text-xs text-slate-500 font-body mt-0.5">Every syllabus PDF that's ever been ingested. Delete bad uploads individually and re-ingest clean ones from the bulk upload panel above.</p>
          </div>
        </div>
        <Button onClick={load} disabled={loading}
          className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-full font-heading text-xs"
          data-testid="library-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Summary label="Total submissions" value={total} testid="library-total" />
        <Summary label="On-screen" value={items.length} testid="library-onscreen" />
        <Summary label="Present on disk" value={`${onDiskCount} / ${items.length}`} testid="library-ondisk" />
        <Summary label="Total size" value={`${totalSizeMb.toFixed(1)} MB`} testid="library-size" />
      </div>

      {/* Filters */}
      <Card className="p-4 rounded-2xl border-slate-100" data-testid="library-filters">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search by filename or chapter…"
              className="pl-8 text-xs h-9 rounded-full border-slate-200"
              data-testid="library-search" />
          </div>
          <select value={boardFilter} onChange={e => setBoardFilter(e.target.value)}
            className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading"
            data-testid="library-board-filter">
            <option value="">All boards</option>
            {BOARDS.map(b => <option key={b.k} value={b.k}>{b.label}</option>)}
          </select>
          <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
            className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading"
            data-testid="library-class-filter">
            <option value="">All classes</option>
            {CLASSES.map(c => <option key={c} value={c}>Class {c}</option>)}
          </select>
          {subjectOptions.length > 0 && (
            <select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)}
              className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading"
              data-testid="library-subject-filter">
              <option value="">All subjects</option>
              {subjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {(q || boardFilter || classFilter || subjectFilter) && (
            <button onClick={() => { setQ(''); setBoardFilter(''); setClassFilter(''); setSubjectFilter(''); }}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline" data-testid="library-clear-filters">
              Clear filters
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 rounded-2xl border-slate-100 overflow-hidden" data-testid="library-table-card">
        {loading && items.length === 0 ? (
          <div className="py-16 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center">
            <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-body">No ingested syllabi match your filters.</p>
            <p className="text-xs text-slate-400 font-body mt-1">Upload PDFs from the panel above to populate the library.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="library-table">
              <thead className="bg-slate-50/80 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Filename</th>
                  <th className="px-3 py-2 text-left">Board · Class · Subject</th>
                  <th className="px-3 py-2 text-left">Chapter</th>
                  <th className="px-3 py-2 text-right">Pages</th>
                  <th className="px-3 py-2 text-right">Chunks</th>
                  <th className="px-3 py-2 text-right">Size</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-center">Uploaded</th>
                  <th className="px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s, i) => (
                  <tr key={s.submission_id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`library-row-${i}`}>
                    <td className="px-3 py-2 max-w-[440px] min-w-[280px]">
                      <p className="font-body text-slate-800 break-all leading-snug" title={s.file_name}>{prettyName(s.file_name) || '—'}</p>
                      <p className="text-[10px] text-slate-400 font-body truncate mt-0.5">{s.submission_id}</p>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge className="bg-indigo-100 text-indigo-700 rounded-full text-[10px] uppercase" data-testid={`library-board-${i}`}>{s.board || '—'}</Badge>
                        <Badge className="bg-slate-100 text-slate-700 rounded-full text-[10px]">Class {s.class_level || '—'}</Badge>
                        <Badge className="bg-emerald-100 text-emerald-700 rounded-full text-[10px]">{s.subject || '—'}</Badge>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-body text-slate-700 max-w-[200px]">
                      <p className="truncate" title={s.topic_name}>{s.topic_name || <span className="text-slate-400 italic">no chapter tag</span>}</p>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600 font-body">{s.pages_rendered ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-600 font-body">{s.chunks_extracted ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-600 font-body">{s.size_mb ? `${s.size_mb.toFixed(1)} MB` : '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {s.on_disk ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-heading uppercase tracking-wider" title="File present on disk"><CheckCircle2 className="w-3.5 h-3.5" /> On disk</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-500 text-[10px] font-heading uppercase tracking-wider" title="File missing from disk"><AlertTriangle className="w-3.5 h-3.5" /> Missing</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-[10px] text-slate-400 font-body">
                      {s.created_at ? s.created_at.slice(0, 10) : '—'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {s.on_disk && (
                          <a href={`${FILE_ORIGIN}${s.pdf_url}`} target="_blank" rel="noreferrer"
                            className="text-indigo-500 hover:text-indigo-700 text-[10px] underline font-heading"
                            data-testid={`library-view-${i}`}>
                            View
                          </a>
                        )}
                        <button onClick={() => deleteOne(s, true)} disabled={deleting[s.submission_id]}
                          className="inline-flex items-center gap-1 text-rose-500 hover:text-rose-700 disabled:opacity-40 text-[10px] font-heading"
                          data-testid={`library-delete-${i}`}
                          title="Delete submission + page images + chunks + file">
                          <Trash2 className="w-3.5 h-3.5" /> {deleting[s.submission_id] ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {total > items.length && (
        <p className="text-[11px] text-slate-400 font-body italic text-center">
          Showing {items.length} of {total} — narrow filters to see more.
        </p>
      )}
    </div>
  );
}

function Summary({ label, value, testid }) {
  return (
    <div className="p-3 rounded-xl bg-white border border-slate-100" data-testid={testid}>
      <p className="text-[10px] font-heading uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-lg font-bold text-slate-900 mt-1">{value}</p>
    </div>
  );
}
